import type { BuxusOperatorPayload } from "../payload/types";
import { toContentSectionTitle } from "../client/contentSectionTitle";
import { getAt } from "../payload/getAt";
import { compactRichHtml } from "../html/compactRichHtml";
import {
  extractManifestFileIds,
  filterPayloadFileRowsInManifest,
  findCatalogFileEntry,
} from "../download/catalogFileLookup";
import type { AssetVisibilityContext } from "./legacyOperatorGates";
import { RADIO_COMMUNICATIONS_SCREEN } from "./legacyScreenTitles";
import { assetIdAndName, getVisibleAssets } from "./legacyOperatorGates";
import type { ResolvedBlock, ResolvedNavItem } from "./resolve";

function rowVisibleForStakeholder(row: { stakeholders?: unknown }, stakeholderCode: string | null): boolean {
  if (stakeholderCode == null || stakeholderCode === "") return true;
  const raw = row as Record<string, unknown>;
  const s = raw.stakeholders ?? raw.Stakeholders;
  if (s == null || !Array.isArray(s) || s.length === 0) return true;
  return s.includes(stakeholderCode);
}

function asContentRows(value: unknown): { content?: string; stakeholders?: unknown }[] {
  if (!Array.isArray(value)) return [];
  return value as { content?: string; stakeholders?: unknown }[];
}

function rowHtmlFromContentField(row: unknown, contentField: string): string {
  if (!row || typeof row !== "object") return "";
  const raw = row as Record<string, unknown>;
  const direct = raw[contentField] ?? raw[contentField.charAt(0).toUpperCase() + contentField.slice(1)];
  if (typeof direct === "string" && direct.trim()) return direct;
  const v = raw.content ?? raw.Content ?? raw.html ?? raw.Html;
  return typeof v === "string" ? v : "";
}

export function findActiveAssetRow(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): Record<string, unknown> | null {
  if (!activeAssetId) return null;
  const list = getVisibleAssets(payload, ctx);
  const row = list.find((a) => {
    const id = (a as Record<string, unknown>).id ?? (a as Record<string, unknown>).Id;
    return typeof id === "string" && id === activeAssetId;
  });
  return row ? (row as Record<string, unknown>) : null;
}

/** Mirrors legacy `checkHasInAnEmergency` (AssetOverviewContainer / screen-access). */
export function checkHasInAnEmergencyAsset(asset: Record<string, unknown>): boolean {
  const emergencyNumber = asset.emergencyNumber ?? asset.EmergencyNumber;
  const sre = asset.stakeholderReportAnEmergency ?? asset.StakeholderReportAnEmergency;
  const hasReportAnEmergency = !!(typeof emergencyNumber === "string" && emergencyNumber.trim()) || !!(
    Array.isArray(sre) && sre.length > 0
  );

  const hasResponderActions = !!(
    (typeof asset.dispatch911 === "string" && asset.dispatch911.trim()) ||
    (typeof asset.fireService === "string" && asset.fireService.trim()) ||
    (typeof asset.lawEnforcement === "string" && asset.lawEnforcement.trim()) ||
    (typeof asset.emergencyMedicalService === "string" && asset.emergencyMedicalService.trim()) ||
    (typeof asset.emergencyManagement === "string" && asset.emergencyManagement.trim()) ||
    (typeof asset.fireCommand === "string" && asset.fireCommand.trim()) ||
    (typeof asset.fireStabilization === "string" && asset.fireStabilization.trim()) ||
    (typeof asset.fireConsiderations === "string" && asset.fireConsiderations.trim()) ||
    (typeof asset.mutualAidInstructions === "string" && asset.mutualAidInstructions.trim())
  );

  const serp = asset.stakeholderEmergencyResponsePlan ?? asset.StakeholderEmergencyResponsePlan;
  const companyActions = asset.companyActionsSubform ?? asset.CompanyActionsSubform;
  const hasCompanyActions =
    !!(Array.isArray(serp) && serp.length > 0) || !!(Array.isArray(companyActions) && companyActions.length > 0);

  const hazards = asset.hazards ?? asset.Hazards;
  const hasHazards = Array.isArray(hazards) && hazards.length > 0;

  return hasReportAnEmergency || hasResponderActions || hasCompanyActions || hasHazards;
}

/** Mirrors legacy `checkHasHazards` (hazard list entry point). */
export function checkHasHazardNavAsset(asset: Record<string, unknown>): boolean {
  const hazards = asset.hazards ?? asset.Hazards;
  if (!Array.isArray(hazards) || hazards.length === 0) return false;
  return hazards.some((h) => {
    if (!h || typeof h !== "object") return false;
    const hz = h as Record<string, unknown>;
    const storage = hz.storageName ?? hz.StorageName;
    const product = hz.product ?? hz.Product;
    const p = product && typeof product === "object" ? (product as Record<string, unknown>) : null;
    const sub = p ? p.productSubType ?? p.ProductSubType : undefined;
    return (typeof storage === "string" && storage.trim()) || (typeof sub === "string" && sub.trim());
  });
}

export function hasOperatorMarkerImagesForAsset(
  operator: Record<string, unknown>,
  asset: Record<string, unknown>,
): boolean {
  const markers = operator.markerImages ?? operator.MarkerImages;
  if (!Array.isArray(markers) || markers.length === 0) return false;
  const assetName = asset.assetName ?? asset.AssetName;
  const assetId = asset.id ?? asset.Id;
  if (typeof assetName !== "string" || typeof assetId !== "string") return false;
  const token = `${assetName} - ${assetId}`;
  return markers.some((m) => {
    if (!m || typeof m !== "object") return false;
    const show = (m as Record<string, unknown>).showInAssets ?? (m as Record<string, unknown>).ShowInAssets;
    if (!Array.isArray(show)) return false;
    return show.some((x) => x === token);
  });
}

/** Mobile line-marker screens: operator-level vs per-asset marker image rows. */
export function markerImageRowVisible(
  row: Record<string, unknown>,
  filter: "operator" | "asset" | undefined,
  asset: Record<string, unknown> | null | undefined,
): boolean {
  if (!filter) return true;
  if (filter === "operator") {
    const shown = row.isShownForOperator ?? row.IsShownForOperator;
    return shown === true;
  }
  if (filter === "asset") {
    if (!asset) return false;
    const show = row.showInAssets ?? row.ShowInAssets;
    if (!Array.isArray(show)) return false;
    const assetName = asset.assetName ?? asset.AssetName;
    const assetId = asset.id ?? asset.Id;
    if (typeof assetName !== "string" || typeof assetId !== "string") return false;
    const token = `${assetName} - ${assetId}`;
    return show.some((x) => x === token);
  }
  return false;
}

/** Payload-only: ERP rows, attachments metadata, or navigable hazards (used as input to nav visibility). */
export function hasAssetDocumentsPayloadQuick(asset: Record<string, unknown>): boolean {
  if (checkHasHazardNavAsset(asset)) return true;
  const erp = asset.stakeholderEmergencyResponsePlan ?? asset.StakeholderEmergencyResponsePlan;
  if (Array.isArray(erp) && erp.length > 0) return true;
  const att = asset.attachmentsSubform ?? asset.AttachmentsSubform;
  if (Array.isArray(att) && att.length > 0) return true;
  return false;
}

/**
 * Mobile / wired local cache: payload lists document rows and files exist on device.
 */
export function hasAssetDocumentsNavVisible(
  asset: Record<string, unknown>,
  assetId: string,
  ctx: Pick<AssetVisibilityContext, "hasLocalAssetDocuments">,
): boolean {
  if (!hasAssetDocumentsPayloadQuick(asset)) return false;
  const localCheck = ctx.hasLocalAssetDocuments;
  if (localCheck == null) return true;
  return localCheck(assetId) === true;
}

/**
 * Sidebar + hub: asset-level and Emergency Response → Documents & Plans use the same rule so both
 * paths stay in sync.
 */
export function hasAssetDocumentsMenuEntryVisible(
  asset: Record<string, unknown>,
  assetId: string,
  ctx: Pick<AssetVisibilityContext, "hasLocalAssetDocuments">,
): boolean {
  if (ctx.hasLocalAssetDocuments != null) {
    return hasAssetDocumentsNavVisible(asset, assetId, ctx);
  }
  return checkHasInAnEmergencyAsset(asset) || hasAssetDocumentsPayloadQuick(asset);
}

export function hasAssetDirectory(asset: Record<string, unknown>): boolean {
  const d = asset.assetDirectorySubform ?? asset.AssetDirectorySubform;
  return Array.isArray(d) && d.length > 0;
}

export function hasGeneralHazards(asset: Record<string, unknown>): boolean {
  const g = asset.generalHazardsSubform ?? asset.GeneralHazardsSubform;
  return Array.isArray(g) && g.length > 0;
}

export function hasSpecialNotes(asset: Record<string, unknown>): boolean {
  const s = asset.specialNotes ?? asset.SpecialNotes;
  return Array.isArray(s) && s.length > 0;
}

export const ASSET_PROFILE_NAV_LABEL = "Asset Profile";
export const FACILITY_PROFILE_NAV_LABEL = "Facility Profile";

/** Legacy mobile: `typeofAsset` includes `"Facility"`. */
export function isFacilityAsset(asset: Record<string, unknown>): boolean {
  const t = asset.typeofAsset ?? asset.TypeofAsset;
  if (!Array.isArray(t)) return false;
  return t.some((x) => typeof x === "string" && x.includes("Facility"));
}

/** Sidebar, hub, and page title for `screen.assetProfile` (single stable route). */
export function assetProfileNavLabel(asset: Record<string, unknown>): string {
  return isFacilityAsset(asset) ? FACILITY_PROFILE_NAV_LABEL : ASSET_PROFILE_NAV_LABEL;
}

function scalarAssetField(asset: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = asset[key];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  return "";
}

export type FacilityProfileCardModel = {
  assetName: string;
  addressLines: string[];
  latLongLabel: string | null;
  canJumpToMap: boolean;
  countiesLabel: string | null;
  emergencyPhone: string | null;
  phoneNumber1: string | null;
  phoneNumber2: string | null;
  facilityDescription: string | null;
};

function facilityCountiesMultiline(asset: Record<string, unknown>): string | null {
  const locs =
    asset.assetLocations ??
    asset.AssetLocations ??
    asset.counties ??
    asset.Counties;
  if (!Array.isArray(locs)) return null;
  const parts: string[] = [];
  for (const loc of locs) {
    if (!loc || typeof loc !== "object") continue;
    const row = loc as Record<string, unknown>;
    const county = row.county ?? row.County;
    const state = row.state ?? row.State;
    if (typeof county === "string" && county.trim() && typeof state === "string" && state.trim()) {
      parts.push(`${county.trim()}, ${state.trim()}`);
    }
  }
  if (!parts.length) return null;
  return [...new Set(parts)].join("\n");
}

/** Legacy mobile `FacilityCard` — shown above the facility field grid. */
export function buildFacilityProfileCardModel(
  asset: Record<string, unknown>,
): FacilityProfileCardModel | null {
  if (!isFacilityAsset(asset)) return null;

  const addressLines: string[] = [];
  const line1 = scalarAssetField(asset, "addressLine1", "AddressLine1");
  const line2 = scalarAssetField(asset, "addressLine2", "AddressLine2");
  const city = scalarAssetField(asset, "city", "City");
  const state = scalarAssetField(asset, "state", "State");
  const zip = scalarAssetField(asset, "zip", "Zip");
  if (line1) addressLines.push(line1);
  if (line2) addressLines.push(line2);
  const cityStateZip = [city, state].filter(Boolean).join(", ") + (zip ? ` ${zip}` : "");
  if (cityStateZip.trim()) addressLines.push(cityStateZip.trim());

  const lat = scalarAssetField(asset, "lat", "Lat");
  const lng = scalarAssetField(asset, "long", "Long", "lng", "Lng");
  const zoomRaw = asset.zoomLevel ?? asset.ZoomLevel;
  const zoom =
    typeof zoomRaw === "number"
      ? zoomRaw
      : typeof zoomRaw === "string" && zoomRaw.trim()
        ? Number(zoomRaw)
        : NaN;
  const latLongLabel = lat && lng ? `${lat}, ${lng}` : null;
  const canJumpToMap = !!(lat && lng && Number.isFinite(zoom));

  return {
    assetName: assetRowDisplayName(asset),
    addressLines,
    latLongLabel,
    canJumpToMap,
    countiesLabel: facilityCountiesMultiline(asset),
    emergencyPhone: scalarAssetField(asset, "emergencyNumber", "EmergencyNumber") || null,
    phoneNumber1: scalarAssetField(asset, "phoneNumber1", "PhoneNumber1") || null,
    phoneNumber2: scalarAssetField(asset, "phoneNumber2", "PhoneNumber2") || null,
    facilityDescription: scalarAssetField(asset, "assetDescription", "AssetDescription") || null,
  };
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function joinLines(values: unknown[] | undefined): string {
  if (!Array.isArray(values)) return "";
  return values.filter((x) => typeof x === "string" && x.trim()).join("\n");
}

function fieldRow(label: string, value: string): string {
  if (!value.trim()) return "";
  return `<tr><th scope="row">${esc(label)}</th><td class="buxus-data-card-multiline">${esc(value)}</td></tr>`;
}

/** Asset display name from a payload row (shared by web header + menus). */
export function assetRowDisplayName(asset: Record<string, unknown>): string {
  const nameRaw = asset.assetName ?? asset.AssetName;
  return typeof nameRaw === "string" && nameRaw.trim() ? nameRaw.trim() : "Asset";
}

function qualifyingHazards(asset: Record<string, unknown>): Record<string, unknown>[] {
  const hazards = asset.hazards ?? asset.Hazards;
  if (!Array.isArray(hazards)) return [];
  return hazards.filter((h) => {
    if (!h || typeof h !== "object") return false;
    const hz = h as Record<string, unknown>;
    const storage = hz.storageName ?? hz.StorageName;
    const product = hz.product ?? hz.Product;
    const p = product && typeof product === "object" ? (product as Record<string, unknown>) : null;
    const sub = p ? p.productSubType ?? p.ProductSubType : undefined;
    return (typeof storage === "string" && storage.trim()) || (typeof sub === "string" && sub.trim());
  }) as Record<string, unknown>[];
}

export type HazardBrowseRow = {
  hazardId: string;
  label: string;
  productSubType: string | null;
};

export type HazardBrowseSection = {
  heading: string;
  rows: HazardBrowseRow[];
};

/** Mobile `HazardsListScreen` tab data (By Location / By Product). */
export type ProductsHazardsBrowseModel = {
  isFacility: boolean;
  byLocation: HazardBrowseSection[];
  byProduct: HazardBrowseSection[];
};

type NormalizedHazardRow = {
  hazardId: string;
  storageName: string;
  productSubType: string | null;
};

function hazardIdFromRow(hz: Record<string, unknown>): string | null {
  const id = hz.id ?? hz.Id;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

function productSubTypeFromRow(hz: Record<string, unknown>): string | null {
  const product = hz.product ?? hz.Product;
  if (!product || typeof product !== "object") return null;
  const pr = product as Record<string, unknown>;
  const sub = pr.productSubType ?? pr.ProductSubType;
  return typeof sub === "string" && sub.trim() ? sub.trim() : null;
}

/** Mirrors mobile `hazardsModified` + `sortArrayByProperty(..., 'storageName')`. */
function normalizedHazardRows(asset: Record<string, unknown>): NormalizedHazardRow[] {
  const rows: NormalizedHazardRow[] = [];
  for (const hz of qualifyingHazards(asset)) {
    const hazardId = hazardIdFromRow(hz);
    if (!hazardId) continue;
    const storageRaw = hz.storageName ?? hz.StorageName;
    const storageName =
      (typeof storageRaw === "string" && storageRaw.trim()) ||
      productSubTypeFromRow(hz) ||
      "";
    if (!storageName) continue;
    rows.push({
      hazardId,
      storageName,
      productSubType: productSubTypeFromRow(hz),
    });
  }
  rows.sort((a, b) =>
    a.storageName.localeCompare(b.storageName, undefined, { sensitivity: "base" }),
  );
  return rows;
}

function buildBrowseByLocation(rows: NormalizedHazardRow[]): HazardBrowseSection[] {
  const headings = [...new Set(rows.map((r) => r.storageName))].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" }),
  );
  return headings.map((heading) => ({
    heading,
    rows: rows
      .filter((r) => r.storageName === heading)
      .map((r) => ({
        hazardId: r.hazardId,
        label: r.productSubType || r.storageName,
        productSubType: r.productSubType,
      })),
  }));
}

function buildBrowseByProduct(
  rows: NormalizedHazardRow[],
  isFacility: boolean,
): HazardBrowseSection[] {
  const subs = [
    ...new Set(rows.map((r) => r.productSubType).filter((s): s is string => !!s)),
  ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  return subs.map((heading) => ({
    heading: isFacility ? heading : "",
    rows: rows
      .filter((r) => r.productSubType === heading)
      .map((r) => ({
        hazardId: r.hazardId,
        label: r.storageName || r.productSubType || heading,
        productSubType: r.productSubType,
      })),
  }));
}

export function buildProductsHazardsBrowse(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ProductsHazardsBrowseModel | null {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return null;
  const rows = normalizedHazardRows(asset);
  if (!rows.length) return null;
  const isFacility = isFacilityAsset(asset);
  return {
    isFacility,
    byLocation: buildBrowseByLocation(rows),
    byProduct: buildBrowseByProduct(rows, isFacility),
  };
}

export function buildAssetProfileDetailsHtml(asset: Record<string, unknown>): string | null {
  const facility = isFacilityAsset(asset);
  const rows: string[] = [];

  if (facility) {
    rows.push(
      fieldRow("Type of Facility", joinLines((asset.facilityType ?? asset.FacilityType) as unknown[])),
    );
    rows.push(fieldRow("Type of Pipe or Asset", joinLines((asset.typeofAsset ?? asset.TypeofAsset) as unknown[])));
    const status = (asset.status ?? asset.Status) as unknown;
    rows.push(fieldRow("Status", typeof status === "string" ? status : ""));
    rows.push(
      fieldRow(
        "Maximum Occupancy",
        scalarAssetField(asset, "maxNumberofOccupants", "MaxNumberofOccupants"),
      ),
    );
    const manned = asset.mannedUnmanned ?? asset.MannedUnmanned;
    rows.push(fieldRow("Manned/Unmanned", typeof manned === "string" ? manned : ""));
    const tri = asset.tRIFacilityNumber ?? asset.TRIFacilityNumber;
    rows.push(fieldRow("Toxic Release Inventory Number", typeof tri === "string" ? tri : ""));
    const rmp = asset.rMPFacilityNumber ?? asset.RMPFacilityNumber;
    rows.push(fieldRow("Risk Management Program Number", typeof rmp === "string" ? rmp : ""));
    const ep = asset.emergencyPlanning ?? asset.EmergencyPlanning;
    rows.push(fieldRow("Subject to Emergency Planning", typeof ep === "string" ? ep : ""));
    const cap = asset.chemicalAccidentPrevention ?? asset.ChemicalAccidentPrevention;
    rows.push(fieldRow("Subject to Chemical Accident Prevention", typeof cap === "string" ? cap : ""));
    const naics = asset.naics ?? asset.Naics;
    rows.push(fieldRow("NAICS Code", typeof naics === "string" ? naics : ""));
    const duns = asset.duns ?? asset.Duns;
    rows.push(fieldRow("DUNS Number", typeof duns === "string" ? duns : ""));
  } else {
    rows.push(fieldRow("Type of Pipe or Asset", joinLines((asset.typeofAsset ?? asset.TypeofAsset) as unknown[])));
    const status = (asset.status ?? asset.Status) as unknown;
    rows.push(fieldRow("Status", typeof status === "string" ? status : ""));
    rows.push(fieldRow("Pipe Material", joinLines((asset.pipeMaterial ?? asset.PipeMaterial) as unknown[])));
    rows.push(
      fieldRow(
        "Products Transported",
        joinLines((asset.productTransported ?? asset.ProductTransported) as unknown[]),
      ),
    );
    const pd = asset.pipeDiameter ?? asset.PipeDiameter;
    rows.push(fieldRow("Pipe Diameter (in)", typeof pd === "string" ? pd : ""));
    const pmin = asset.pipeDiameterMin ?? asset.PipeDiameterMin;
    const pmax = asset.pipeDiameterMax ?? asset.PipeDiameterMax;
    if ((typeof pmin === "string" && pmin) || (typeof pmax === "string" && pmax)) {
      rows.push(fieldRow("Pipe Diameter Range (in)", `${pmin ?? ""} - ${pmax ?? ""}`.trim()));
    }
    const pressure = asset.pressure ?? asset.Pressure;
    rows.push(fieldRow("Pressure", typeof pressure === "string" && pressure ? `${pressure} PSIG` : ""));
    const prMin = asset.pressureMin ?? asset.PressureMin;
    const prMax = asset.pressureMax ?? asset.PressureMax;
    if ((typeof prMin === "string" && prMin) || (typeof prMax === "string" && prMax)) {
      rows.push(fieldRow("Pressure Range", `${prMin ?? ""} - ${prMax ?? ""} PSIG`.trim()));
    }
  }

  const body = rows.filter(Boolean).join("");
  if (!body) return null;
  const assetName = assetRowDisplayName(asset);
  const title =
    !facility && assetName
      ? `<p class="buxus-data-card-title">${esc(assetName)}</p>`
      : "";
  return `${title}<table class="buxus-data-card-table"><tbody>${body}</tbody></table>`;
}

export function buildAssetOverviewHubNav(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedNavItem[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset || !activeAssetId) return [];
  const op = payload.operator as Record<string, unknown>;
  const id = activeAssetId;
  const items: ResolvedNavItem[] = [
    {
      label: assetProfileNavLabel(asset),
      targetScreenId: "screen.assetProfile",
      assetId: id,
    },
  ];
  if (checkHasInAnEmergencyAsset(asset)) {
    items.push({ label: "Emergency Response", targetScreenId: "screen.assetEmergencyResponse", assetId: id });
  }
  if (hasOperatorMarkerImagesForAsset(op, asset)) {
    items.push({ label: "Line Markers", targetScreenId: "screen.assetLineMarkers", assetId: id });
  }
  if (checkHasHazardNavAsset(asset)) {
    items.push({ label: "Products & Hazards", targetScreenId: "screen.assetProductsHazards", assetId: id });
  }
  if (hasAssetDirectory(asset)) {
    items.push({ label: "Key Locations", targetScreenId: "screen.assetKeyLocations", assetId: id });
  }
  if (hasAssetDocumentsMenuEntryVisible(asset, id, ctx)) {
    items.push({ label: "Documents & Plans", targetScreenId: "screen.assetDocuments", assetId: id });
  }
  if (hasGeneralHazards(asset)) {
    items.push({ label: "General Hazards", targetScreenId: "screen.assetGeneralHazards", assetId: id });
  }
  if (hasSpecialNotes(asset)) {
    items.push({ label: "Special Notes", targetScreenId: "screen.assetSpecialNotes", assetId: id });
  }
  return items;
}

function mergeStakeholderHtml(
  asset: Record<string, unknown>,
  path: string,
  stakeholderCode: string | null,
  contentField: string,
): string {
  const rows = asContentRows(getAt(asset, path)).filter((r) => rowVisibleForStakeholder(r, stakeholderCode));
  const parts = rows.map((r) => rowHtmlFromContentField(r, contentField)).filter((x) => x.trim());
  return parts.join('<hr style="margin:1.25rem 0;border:none;border-top:1px solid #e2e8f0"/>');
}

function labeledHtmlSection(heading: string, html: string): string {
  if (!html.trim()) return "";
  const h = esc(toContentSectionTitle(heading));
  return `<h3 style="font-size:13px;color:#64748b;font-weight:600;margin:1.35rem 0 0.55rem;letter-spacing:0.02em;line-height:1.3">${h}</h3>${compactRichHtml(html)}`;
}

function hasStakeholderSubformContent(
  asset: Record<string, unknown>,
  path: string,
  stakeholderCode: string | null,
): boolean {
  const rows = asContentRows(getAt(asset, path));
  return rows.some(
    (r) => rowVisibleForStakeholder(r, stakeholderCode) && rowHtmlFromContentField(r, "content").trim(),
  );
}

export type HazardDetailHtmlBlock = { heading: string; html: string };

function hazardHtmlBlock(heading: string, raw: unknown): HazardDetailHtmlBlock | null {
  const t = typeof raw === "string" ? raw.trim() : "";
  if (!t) return null;
  if (t.includes("<")) return { heading, html: compactRichHtml(t) };
  return {
    heading,
    html: `<p style="margin:0;line-height:1.5">${esc(t)}</p>`,
  };
}

function scalarHtmlParagraph(label: string, raw: unknown): string {
  if (typeof raw !== "string" || !raw.trim()) return "";
  const t = raw.trim();
  if (t.includes("<")) return labeledHtmlSection(label, t);
  return `<p style="margin:0 0 12px"><span style="color:#64748b;font-weight:600">${esc(toContentSectionTitle(label))}: </span>${esc(t)}</p>`;
}

function hasEmergencyReport(asset: Record<string, unknown>): boolean {
  const emergencyNumber = asset.emergencyNumber ?? asset.EmergencyNumber;
  const sre = asset.stakeholderReportAnEmergency ?? asset.StakeholderReportAnEmergency;
  return (
    (typeof emergencyNumber === "string" && emergencyNumber.trim().length > 0) ||
    (Array.isArray(sre) && sre.length > 0)
  );
}

function hasEmergencySteps(asset: Record<string, unknown>, stakeholderCode: string | null): boolean {
  return hasStakeholderSubformContent(
    asset,
    "generalDosAndDoNotsSubform",
    stakeholderCode,
  );
}

function hasEmergencyResponder(asset: Record<string, unknown>): boolean {
  return !!(
    (typeof asset.dispatch911 === "string" && asset.dispatch911.trim()) ||
    (typeof asset.fireService === "string" && asset.fireService.trim()) ||
    (typeof asset.lawEnforcement === "string" && asset.lawEnforcement.trim()) ||
    (typeof asset.emergencyMedicalService === "string" && asset.emergencyMedicalService.trim()) ||
    (typeof asset.emergencyManagement === "string" && asset.emergencyManagement.trim()) ||
    (typeof asset.mutualAidInstructions === "string" && asset.mutualAidInstructions.trim())
  );
}

function hasEmergencyCompanyResponse(asset: Record<string, unknown>): boolean {
  const serp = asset.stakeholderEmergencyResponsePlan ?? asset.StakeholderEmergencyResponsePlan;
  const oj = asset.operatorObjectivesSubform ?? asset.OperatorObjectivesSubform;
  const cap = asset.operatorCapabilitiesSubform ?? asset.OperatorCapabilitiesSubform;
  const ca = asset.companyActionsSubform ?? asset.CompanyActionsSubform;
  return (
    (Array.isArray(serp) && serp.length > 0) ||
    (Array.isArray(oj) && oj.length > 0) ||
    (Array.isArray(cap) && cap.length > 0) ||
    (Array.isArray(ca) && ca.length > 0)
  );
}

function hasEmergencyCommunications(asset: Record<string, unknown>): boolean {
  const html = asset.communicationsDescription ?? asset.CommunicationsDescription;
  const rf = asset.radioFrequencySubform ?? asset.RadioFrequencySubform;
  return (
    (typeof html === "string" && html.trim().length > 0) || (Array.isArray(rf) && rf.length > 0)
  );
}

function hasEmergencyFireSuppression(
  asset: Record<string, unknown>,
  stakeholderCode: string | null,
): boolean {
  return hasStakeholderSubformContent(asset, "fireSuppressionSystemSubform", stakeholderCode);
}

function hasEmergencyWorstCase(asset: Record<string, unknown>): boolean {
  return !!(
    (typeof asset.worstCaseToxicRelease === "string" && asset.worstCaseToxicRelease.trim()) ||
    (typeof asset.mostHazardousAlternativeRelease === "string" && asset.mostHazardousAlternativeRelease.trim()) ||
    (typeof asset.worstCaseFlammableRelease === "string" && asset.worstCaseFlammableRelease.trim()) ||
    (typeof asset.mostHazardousAlternativeFlammableRelease === "string" &&
      asset.mostHazardousAlternativeFlammableRelease.trim()) ||
    (typeof asset.worstCaseReactivity === "string" && asset.worstCaseReactivity.trim()) ||
    (typeof asset.worstCaseReactivityAlternativeScenario === "string" &&
      asset.worstCaseReactivityAlternativeScenario.trim())
  );
}

export type AssetEmergencySubpage = {
  menuId: string;
  label: string;
  screenId: string;
};

/** Screens canonical at asset root; web sidebar omits ER-folder copies (ER hub content still links here). */
export const ASSET_ROOT_CANONICAL_ER_SCREEN_IDS = new Set([
  "screen.assetProductsHazards",
  "screen.assetDocuments",
]);

/** Emergency Response subpages for ER hub content (full list, mirrors mobile InAnEmergencyScreen). */
export function listAssetEmergencySubpages(
  asset: Record<string, unknown>,
  assetId: string,
  ctx: AssetVisibilityContext,
): AssetEmergencySubpage[] {
  const stakeholderCode = ctx.stakeholderCode;
  const items: AssetEmergencySubpage[] = [];
  if (hasEmergencyReport(asset)) {
    items.push({
      menuId: "sk.er.report",
      label: "Report an Emergency",
      screenId: "screen.assetEmergencyReport",
    });
  }
  if (hasEmergencySteps(asset, stakeholderCode)) {
    items.push({ menuId: "sk.er.steps", label: "Steps to Take", screenId: "screen.assetEmergencySteps" });
  }
  if (hasEmergencyResponder(asset)) {
    items.push({
      menuId: "sk.er.responder",
      label: "Responder Actions",
      screenId: "screen.assetEmergencyResponder",
    });
  }
  if (hasEmergencyCompanyResponse(asset)) {
    items.push({
      menuId: "sk.er.company",
      label: "Company Response",
      screenId: "screen.assetEmergencyCompany",
    });
  }
  if (checkHasHazardNavAsset(asset)) {
    items.push({
      menuId: "sk.er.products",
      label: "Products & Hazards",
      screenId: "screen.assetProductsHazards",
    });
  }
  if (hasAssetDirectory(asset)) {
    items.push({
      menuId: "sk.er.keyLocations",
      label: "Key Locations",
      screenId: "screen.assetEmergencyKeyLocations",
    });
  }
  if (hasEmergencyCommunications(asset)) {
    items.push({
      menuId: "sk.er.communications",
      label: RADIO_COMMUNICATIONS_SCREEN,
      screenId: "screen.assetEmergencyCommunications",
    });
  }
  if (hasEmergencyFireSuppression(asset, stakeholderCode)) {
    items.push({
      menuId: "sk.er.fireSuppression",
      label: "Fire Suppression",
      screenId: "screen.assetEmergencyFireSuppression",
    });
  }
  if (hasEmergencyWorstCase(asset)) {
    items.push({
      menuId: "sk.er.worstCase",
      label: "Worst Case Scenarios",
      screenId: "screen.assetEmergencyWorstCase",
    });
  }
  if (hasAssetDocumentsMenuEntryVisible(asset, assetId, ctx)) {
    items.push({
      menuId: "sk.er.documents",
      label: "Documents & Plans",
      screenId: "screen.assetDocuments",
    });
  }
  return items;
}

/** Web sidebar ER folder: same as hub list minus asset-root-canonical screens. */
export function listAssetEmergencySidebarSubpages(
  asset: Record<string, unknown>,
  assetId: string,
  ctx: AssetVisibilityContext,
): AssetEmergencySubpage[] {
  return listAssetEmergencySubpages(asset, assetId, ctx).filter(
    (sp) => !ASSET_ROOT_CANONICAL_ER_SCREEN_IDS.has(sp.screenId),
  );
}

export function buildAssetEmergencyHubNavItems(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedNavItem[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset || !activeAssetId) return [];
  return listAssetEmergencySubpages(asset, activeAssetId, ctx).map((sp) => ({
    label: sp.label,
    targetScreenId: sp.screenId,
    assetId: activeAssetId,
  }));
}

export function buildAssetEmergencyReportBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return [];
  const sc = ctx.stakeholderCode;
  const blocks: ResolvedBlock[] = [];
  const emergencyNumber = asset.emergencyNumber ?? asset.EmergencyNumber;
  if (typeof emergencyNumber === "string" && emergencyNumber.trim()) {
    blocks.push({ kind: "emergencyCallButton", phone: emergencyNumber.trim() });
  }
  const sreHtml = mergeStakeholderHtml(asset, "stakeholderReportAnEmergency", sc, "emergencyInstructions");
  if (sreHtml.trim()) {
    blocks.push({ kind: "richHtml", html: compactRichHtml(sreHtml) });
  }
  if (!blocks.length) return [];
  return blocks;
}

export function buildAssetEmergencyStepsBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return [];
  const html = mergeStakeholderHtml(
    asset,
    "generalDosAndDoNotsSubform",
    ctx.stakeholderCode,
    "content",
  );
  if (!html.trim()) return [];
  return [{ kind: "richHtml", html: compactRichHtml(html) }];
}

export type ResponderActionsAccordionSection = {
  id: string;
  title: string;
  html: string;
};

export function buildAssetEmergencyResponderBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return [];
  const sections: ResponderActionsAccordionSection[] = [];
  for (const { key, id, title } of [
    { key: "dispatch911", id: "dispatch-911", title: "911 Dispatch" },
    { key: "fireService", id: "fire-service", title: "Fire Service" },
    { key: "lawEnforcement", id: "law-enforcement", title: "Law Enforcement" },
    { key: "emergencyMedicalService", id: "ems", title: "EMS" },
    { key: "emergencyManagement", id: "emergency-management", title: "Emergency Management" },
    { key: "mutualAidInstructions", id: "mutual-aid", title: "Mutual Aid" },
  ] as const) {
    const raw = asset[key] ?? asset[key.charAt(0).toUpperCase() + key.slice(1)];
    if (typeof raw === "string" && raw.trim()) {
      sections.push({ id, title, html: compactRichHtml(raw.trim()) });
    }
  }
  if (!sections.length) return [];
  return [{ kind: "responderActionsAccordions", sections }];
}

export type CompanyResponseAccordionSection = {
  id: string;
  title: string;
  html: string;
};

export function hasAssetEmergencyCompanyScreenContent(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): boolean {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return false;
  const sc = ctx.stakeholderCode;

  if (erpRowsToPickerItems(buildOpenableCompanyErpRows(asset, ctx)).length > 0) {
    return true;
  }

  for (const path of [
    "companyActionsSubform",
    "reasonstoNotifySubform",
    "operatorCapabilitiesSubform",
    "operatorObjectivesSubform",
  ]) {
    if (mergeStakeholderHtml(asset, path, sc, "content").trim()) return true;
  }
  return false;
}

export function buildAssetEmergencyCompanyBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return [];
  const sc = ctx.stakeholderCode;
  const blocks: ResolvedBlock[] = [];

  const erpItems = erpRowsToPickerItems(buildOpenableCompanyErpRows(asset, ctx));
  if (erpItems.length) {
    blocks.push({ kind: "companyErpPicker", items: erpItems });
  }

  const accordionSections: CompanyResponseAccordionSection[] = [];
  for (const { path, id, title } of [
    { path: "companyActionsSubform", id: "actions", title: "Actions" },
    { path: "reasonstoNotifySubform", id: "reasons-to-notify", title: "Reasons to Notify" },
    { path: "operatorCapabilitiesSubform", id: "capabilities", title: "Capabilities" },
    { path: "operatorObjectivesSubform", id: "objectives", title: "Objectives" },
  ] as const) {
    const html = mergeStakeholderHtml(asset, path, sc, "content");
    if (html.trim()) {
      accordionSections.push({ id, title, html: compactRichHtml(html) });
    }
  }
  if (accordionSections.length) {
    blocks.push({ kind: "companyResponseAccordions", sections: accordionSections });
  }

  if (!blocks.length) return [];
  return blocks;
}

export type RadioFrequencyAccordionSection = {
  id: string;
  title: string;
  subtitle: string;
  frequency: string;
  system: string;
  description: string;
  purpose: string;
};

export type KeyLocationAccordionSection = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  phoneNumber1: string;
  phoneNumber2: string;
  mapCode: string;
  lat: string;
  lng: string;
  zoomLevel: string;
};

export function buildAssetEmergencyCommunicationsBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return [];
  const blocks: ResolvedBlock[] = [];
  const comm = asset.communicationsDescription ?? asset.CommunicationsDescription;
  if (typeof comm === "string" && comm.trim()) {
    blocks.push({ kind: "richHtml", html: compactRichHtml(comm.trim()) });
  }
  const rf = asset.radioFrequencySubform ?? asset.RadioFrequencySubform;
  const sections: RadioFrequencyAccordionSection[] = [];
  if (Array.isArray(rf)) {
    rf.forEach((row, index) => {
      if (!row || typeof row !== "object") return;
      const r = row as Record<string, unknown>;
      const channel = hazardStr(r.channel ?? r.Channel);
      const system = hazardStr(r.system ?? r.System);
      const description = hazardStr(r.description ?? r.Description);
      const purpose = hazardStr(r.purpose ?? r.Purpose);
      if (!channel && !system && !description && !purpose) return;
      sections.push({
        id: `radio-${index}`,
        title: channel || "Radio",
        subtitle: system,
        frequency: channel,
        system,
        description,
        purpose,
      });
    });
  }
  if (sections.length) {
    blocks.push({ kind: "radioFrequencyAccordions", sections });
  }
  return blocks;
}

export function buildAssetEmergencyFireSuppressionBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return [];
  const html = mergeStakeholderHtml(
    asset,
    "fireSuppressionSystemSubform",
    ctx.stakeholderCode,
    "content",
  );
  if (!html.trim()) return [];
  return [{ kind: "richHtml", html: compactRichHtml(html) }];
}

export function buildAssetEmergencyWorstCaseBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return [];
  const parts: string[] = [];
  const push = (label: string, key: string) => {
    const v = asset[key] ?? asset[key.charAt(0).toUpperCase() + key.slice(1)];
    const p = scalarHtmlParagraph(label, v);
    if (p) parts.push(p);
  };
  push("Worst case toxic release", "worstCaseToxicRelease");
  push("Most hazardous alternative release", "mostHazardousAlternativeRelease");
  push("Worst case flammable release", "worstCaseFlammableRelease");
  push("Most hazardous alternative flammable release", "mostHazardousAlternativeFlammableRelease");
  push("Worst case reactivity", "worstCaseReactivity");
  push("Worst case reactivity (alternative scenario)", "worstCaseReactivityAlternativeScenario");
  if (!parts.length) return [];
  return [{ kind: "richHtml", html: parts.join("") }];
}

export function buildAssetEmergencyKeyLocationsBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  return buildAssetDirectoryListBlocks(payload, ctx, activeAssetId);
}

function hazardLabel(h: Record<string, unknown>): string {
  const storage = h.storageName ?? h.StorageName;
  if (typeof storage === "string" && storage.trim()) return storage.trim();
  const product = h.product ?? h.Product;
  if (product && typeof product === "object") {
    const p = product as Record<string, unknown>;
    const sub = p.productSubType ?? p.ProductSubType;
    if (typeof sub === "string" && sub.trim()) return sub.trim();
    const chem = p.cASChemicalName ?? p.CASChemicalName ?? p.nonEHSName ?? p.NonEHSName;
    if (typeof chem === "string" && chem.trim()) return chem.trim();
  }
  const ht = h.hazardType ?? h.HazardType;
  if (typeof ht === "string" && ht.trim()) return ht.trim();
  return "Hazard";
}

export type AssetDocumentsFileRow = {
  id: string;
  title: string;
  subtitle?: string;
  fileExt?: string;
  /** Payload root filename for download (`fileName`, `documentPath`, or title + ext). */
  fileName?: string;
};

export type AssetDocumentsSection = {
  title: string;
  rows: AssetDocumentsFileRow[];
};

/** Mobile `DocumentsScreen` list shape (ERP → SDS → attachment categories). */
export type AssetDocumentsListModel = {
  assetDisplayName: string;
  showWebPlaceholderNotice: boolean;
  sections: AssetDocumentsSection[];
};

function docFieldStr(v: unknown): string {
  if (typeof v === "string") return v.trim();
  return "";
}

function docFileExt(row: Record<string, unknown>): string | undefined {
  const raw = docFieldStr(row.fileExt ?? row.FileExt);
  if (!raw) return undefined;
  return raw.startsWith(".") ? raw : `.${raw}`;
}

/** Download basename: payload `fileName` when set, else title + `fileExt` (not `documentPath` — that is a Zoho URL). */
function docPayloadFileName(row: Record<string, unknown>, title: string): string | undefined {
  const explicit = docFieldStr(row.fileName ?? row.FileName);
  if (explicit) return explicit;
  const ext = docFileExt(row);
  if (!title) return undefined;
  if (!ext) return title;
  if (title.toLowerCase().endsWith(ext.toLowerCase())) return title;
  return `${title}${ext}`;
}

function compareDocLabels(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function buildSdsDocumentRows(
  asset: Record<string, unknown>,
): AssetDocumentsFileRow[] {
  const hazards = asset.hazards ?? asset.Hazards;
  if (!Array.isArray(hazards)) return [];
  const rows: { sortKey: string; row: AssetDocumentsFileRow }[] = [];
  for (const h of hazards) {
    if (!h || typeof h !== "object") continue;
    const hz = h as Record<string, unknown>;
    const product = hz.product ?? hz.Product;
    const p =
      product && typeof product === "object" ? (product as Record<string, unknown>) : null;
    const sub = p ? docFieldStr(p.productSubType ?? p.ProductSubType) : "";
    const storage = docFieldStr(hz.storageName ?? hz.StorageName);
    if (!storage && !sub) continue;
    const displayName = storage || sub;
    const id = docFieldStr(hz.id ?? hz.Id);
    if (!id) continue;
    rows.push({
      sortKey: displayName,
      row: {
        id,
        title: displayName,
        subtitle: sub || undefined,
        fileExt: docFileExt(hz),
        fileName: docPayloadFileName(hz, displayName),
      },
    });
  }
  rows.sort((a, b) => compareDocLabels(a.sortKey, b.sortKey));
  return rows.map((x) => x.row);
}

/** Payload ERP rows only — manifest + catalog/disk gates apply later (mobile DocumentsScreen parity). */
function buildErpDocumentRows(asset: Record<string, unknown>): AssetDocumentsFileRow[] {
  const erp = asset.stakeholderEmergencyResponsePlan ?? asset.StakeholderEmergencyResponsePlan;
  if (!Array.isArray(erp)) return [];
  const rows: AssetDocumentsFileRow[] = [];
  for (const row of erp) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const title = docFieldStr(r.title ?? r.Title);
    const id = docFieldStr(r.id ?? r.Id);
    if (!title || !id) continue;
    const docType = docFieldStr(r.documentType ?? r.DocumentType);
    rows.push({
      id,
      title,
      subtitle: docType || undefined,
      fileExt: docFileExt(r),
      fileName: docPayloadFileName(r, title),
    });
  }
  rows.sort((a, b) => compareDocLabels(a.title, b.title));
  return rows;
}

/** Payload attachment rows only — not manifest-filtered on mobile; catalog/disk gates apply later. */
function buildAttachmentDocumentSections(asset: Record<string, unknown>): AssetDocumentsSection[] {
  const att = asset.attachmentsSubform ?? asset.AttachmentsSubform;
  if (!Array.isArray(att)) return [];
  const byCategory = new Map<string, AssetDocumentsFileRow[]>();
  for (const row of att) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const title = docFieldStr(r.title ?? r.Title);
    const id = docFieldStr(r.id ?? r.Id);
    const category = docFieldStr(r.category ?? r.Category) || "Attachments";
    if (!title || !id) continue;
    const list = byCategory.get(category) ?? [];
    list.push({
      id,
      title,
      fileExt: docFileExt(r),
      fileName: docPayloadFileName(r, title),
    });
    byCategory.set(category, list);
  }
  const categories = [...byCategory.keys()].sort(compareDocLabels);
  return categories.map((category) => {
    const rows = byCategory.get(category) ?? [];
    rows.sort((a, b) => compareDocLabels(a.title, b.title));
    return { title: category, rows };
  });
}

/**
 * Web substitute for mobile `getExistingFiles`: row must be in operator catalog with SAS and ext.
 * Hazards and attachments are not manifest-filtered; ERP rows are manifest-filtered before this step.
 */
function openableCatalogDocumentRow(
  row: AssetDocumentsFileRow,
  catalogOperatorRow: Record<string, unknown>,
): AssetDocumentsFileRow | null {
  const entry = findCatalogFileEntry(catalogOperatorRow, row.id, row.fileExt);
  if (!entry) return null;
  const ext = row.fileExt?.trim() || entry.ext;
  if (!ext) return null;
  return {
    ...row,
    fileExt: ext.startsWith(".") ? ext : `.${ext}`,
  };
}

/** List rows from payload when catalog is not in memory yet (open still waits on catalog SAS). */
function listOpenableDocumentRow(
  row: AssetDocumentsFileRow,
  catalogOperatorRow: Record<string, unknown> | null | undefined,
): AssetDocumentsFileRow | null {
  if (catalogOperatorRow) return openableCatalogDocumentRow(row, catalogOperatorRow);
  const ext = row.fileExt?.trim();
  if (!ext) return null;
  return {
    ...row,
    fileExt: ext.startsWith(".") ? ext : `.${ext}`,
  };
}

function resolveManifestFileIds(ctx: AssetVisibilityContext): Set<string> | undefined {
  const fromCatalog = extractManifestFileIds(ctx.catalogOperatorRow);
  if (fromCatalog) return fromCatalog;
  if (ctx.persistedManifestFileIds?.size) return new Set(ctx.persistedManifestFileIds);
  return undefined;
}

function filterDocumentSectionsToCatalog(
  sections: AssetDocumentsSection[],
  catalogOperatorRow: Record<string, unknown> | null | undefined,
): AssetDocumentsSection[] {
  return sections
    .map((section) => ({
      ...section,
      rows: section.rows
        .map((row) => listOpenableDocumentRow(row, catalogOperatorRow))
        .filter((row): row is AssetDocumentsFileRow => row != null),
    }))
    .filter((section) => section.rows.length > 0);
}

/** Manifest + catalog gates for Company Response ERP picker (mobile `getFilesInManifest` + open parity). */
function buildOpenableCompanyErpRows(
  asset: Record<string, unknown>,
  ctx: AssetVisibilityContext,
): AssetDocumentsFileRow[] {
  const manifestFileIds = resolveManifestFileIds(ctx);
  if (!manifestFileIds && !ctx.catalogOperatorRow && !ctx.persistedManifestFileIds?.size) {
    return [];
  }
  const erpRows = filterPayloadFileRowsInManifest(buildErpDocumentRows(asset), manifestFileIds);
  return erpRows
    .map((row) => listOpenableDocumentRow(row, ctx.catalogOperatorRow))
    .filter((row): row is AssetDocumentsFileRow => row != null);
}

function erpRowsToPickerItems(rows: AssetDocumentsFileRow[]): {
  id: string;
  label: string;
  fileExt?: string;
  fileName?: string;
}[] {
  return rows.map((row) => ({
    id: row.id,
    label: row.title,
    fileExt: row.fileExt,
    fileName: row.fileName,
  }));
}

export function buildAssetDocumentsListModel(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): AssetDocumentsListModel | null {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return null;
  const manifestFileIds = resolveManifestFileIds(ctx);
  const sections: AssetDocumentsSection[] = [];
  const erpRows = filterPayloadFileRowsInManifest(
    buildErpDocumentRows(asset),
    manifestFileIds,
  );
  if (erpRows.length) {
    sections.push({ title: "Emergency Response Plans", rows: erpRows });
  }
  const sdsRows = buildSdsDocumentRows(asset);
  if (sdsRows.length) {
    sections.push({ title: "SDS Documents", rows: sdsRows });
  }
  sections.push(...buildAttachmentDocumentSections(asset));
  const filteredSections = filterDocumentSectionsToCatalog(sections, ctx.catalogOperatorRow);
  return {
    assetDisplayName: assetRowDisplayName(asset),
    showWebPlaceholderNotice: false,
    sections: filteredSections,
  };
}

/** True when the filtered document list for an asset would show at least one row. */
export function assetHasOpenableDocuments(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  assetId: string,
): boolean {
  const model = buildAssetDocumentsListModel(payload, ctx, assetId);
  return !!model && model.sections.some((section) => section.rows.length > 0);
}

export function buildAssetDocumentsListBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  const model = buildAssetDocumentsListModel(payload, ctx, activeAssetId);
  if (!model) return [];
  if (!model.sections.some((section) => section.rows.length > 0)) return [];
  const blocks: ResolvedBlock[] = [];
  blocks.push({ kind: "assetDocumentsList", model });
  return blocks;
}

export function buildAssetDirectoryListBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return [];
  const rows = asset.assetDirectorySubform ?? asset.AssetDirectorySubform;
  if (!Array.isArray(rows) || rows.length === 0) {
    return [{ kind: "richHtml", html: `<p style="margin:12px 0">No key locations listed.</p>` }];
  }
  const sections: KeyLocationAccordionSection[] = [];
  rows.forEach((row, index) => {
    if (!row || typeof row !== "object") return;
    const r = row as Record<string, unknown>;
    const title = hazardStr(r.area ?? r.Area) || "Location";
    const description = hazardStr(r.description ?? r.Description);
    const phoneNumber1 = hazardStr(r.phoneNumber1 ?? r.PhoneNumber1);
    const phoneNumber2 = hazardStr(r.phoneNumber2 ?? r.PhoneNumber2);
    const mapCode = hazardStr(r.mapCode ?? r.MapCode);
    const lat = hazardStr(r.lat ?? r.Lat);
    const lng = hazardStr(r.long ?? r.Long ?? r.lng ?? r.Lng);
    const zoomLevel = hazardStr(r.zoomLevel ?? r.ZoomLevel);
    if (!description && !phoneNumber1 && !phoneNumber2 && !mapCode && !lat && !lng) return;
    sections.push({
      id: `key-location-${index}`,
      title,
      subtitle: description,
      description,
      phoneNumber1,
      phoneNumber2,
      mapCode,
      lat,
      lng,
      zoomLevel,
    });
  });
  if (!sections.length) {
    return [{ kind: "richHtml", html: `<p style="margin:12px 0">No displayable key location rows.</p>` }];
  }
  return [{ kind: "keyLocationAccordions", sections }];
}

export function buildAssetGeneralHazardsBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return [];
  const html = mergeStakeholderHtml(asset, "generalHazardsSubform", ctx.stakeholderCode, "content");
  if (!html.trim()) return [];
  return [{ kind: "richHtml", html: compactRichHtml(html) }];
}

export function buildAssetSpecialNotesBlocks(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
): ResolvedBlock[] {
  const asset = findActiveAssetRow(payload, ctx, activeAssetId);
  if (!asset) return [];
  const html = mergeStakeholderHtml(asset, "specialNotes", ctx.stakeholderCode, "content");
  if (!html.trim()) return [];
  return [{ kind: "richHtml", html: compactRichHtml(html) }];
}

function hazardStr(v: unknown): string {
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return "";
}

function hazardStrList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .filter((x) => x.length > 0);
}

/** Mobile DataCard accepts string or string[] (e.g. CAS# Availability from Zoho). */
function hazardFieldDisplayValue(v: unknown): string {
  const list = hazardStrList(v);
  if (list.length) return list.join("\n");
  return hazardStr(v);
}

/**
 * Mirrors mobile `HazardsListScreen` hazardsModified:
 * `storageName: x.storageName || x.product?.productSubType`.
 */
function effectiveHazardStorageName(hz: Record<string, unknown>): string {
  const storage = hazardStr(hz.storageName ?? hz.StorageName);
  if (storage) return storage;
  return productSubTypeFromRow(hz) ?? "";
}

/** Mirrors mobile HazardContainer `hasStorageLocation` (loose truthiness, not hazardStr). */
function hazardHasStorageLocation(hz: Record<string, unknown>): boolean {
  if (effectiveHazardStorageName(hz)) return true;
  if (hz.storageDescription ?? hz.StorageDescription) return true;
  if (hz.sDSDocument ?? hz.SDSDocument ?? hz.sdsDocument ?? hz.SdsDocument) return true;
  if (hz.storageMapCode ?? hz.StorageMapCode) return true;
  const lat = hz.storageLat ?? hz.StorageLat;
  const lng = hz.storageLong ?? hz.StorageLong;
  if (lat && lng) return true;
  if (hz.safetyDistance ?? hz.SafetyDistance) return true;
  const amount = hz.amountStored ?? hz.AmountStored;
  return !!amount;
}

/** Mobile treats falsy / zero stored amounts as no storage quantity. */
function hazardHasStoredAmount(hz: Record<string, unknown>): boolean {
  const amount = hz.amountStored ?? hz.AmountStored;
  if (amount === undefined || amount === null) return false;
  if (typeof amount === "number") return Number.isFinite(amount) && amount !== 0;
  const s = hazardStr(amount);
  if (!s) return false;
  const n = parseFloat(s);
  if (Number.isFinite(n)) return n !== 0;
  return true;
}

function hazardProductRow(hz: Record<string, unknown>): Record<string, unknown> | null {
  const product = hz.product ?? hz.Product;
  return product && typeof product === "object" ? (product as Record<string, unknown>) : null;
}

/** Find visible asset id containing `hazardId` (bookmark / hard reload when `activeAssetId` is unset). */
export function findAssetIdByHazardId(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  hazardId: string,
  productSubtype?: string | null,
): string | null {
  const want = hazardId.trim();
  if (!want) return null;
  const subtype = productSubtype?.trim() ?? "";
  for (const row of getVisibleAssets(payload, ctx)) {
    const asset = row as Record<string, unknown>;
    const hz = findHazardRowInAsset(asset, want);
    if (!hz) continue;
    if (subtype) {
      const sub = productSubTypeFromRow(hz);
      if (sub && sub !== subtype) continue;
    }
    const pair = assetIdAndName(asset);
    return pair?.id ?? null;
  }
  return null;
}

/** Resolve a hazard row on an asset by CRM id (`hi` query on web). */
export function findHazardRowInAsset(
  asset: Record<string, unknown>,
  hazardId: string,
): Record<string, unknown> | null {
  const want = hazardId.trim();
  if (!want) return null;
  const hazards = asset.hazards ?? asset.Hazards;
  if (!Array.isArray(hazards)) return null;
  for (const h of hazards) {
    if (!h || typeof h !== "object") continue;
    const hz = h as Record<string, unknown>;
    if (hazardIdFromRow(hz) === want) return hz;
  }
  return null;
}

export type HazardDetailFieldRow = { label: string; value: string };

export type HazardDetailMixtureRow = {
  productName: string;
  percentage: string;
  casNumber: string;
  ehs: string;
};

export type HazardDetailLeakImage = {
  id: string;
  title: string;
  description: string;
  group: "sight" | "smell" | "sound";
  fileExt?: string;
};

export type HazardDetailSection = {
  id: string;
  title: string;
  html?: string;
  /** Mobile `HtmlListItem`-style sub-blocks (e.g. Appearance / Character under Leak Recognition). */
  htmlBlocks?: HazardDetailHtmlBlock[];
  /** Mobile `DataCard` rows — label left, value right (Product Details, storage metadata). */
  fields?: HazardDetailFieldRow[];
  /** Mobile `HazardStorageLocationCard` rows — grey label bar with value stacked below. */
  stackedFields?: HazardDetailFieldRow[];
  mixture?: HazardDetailMixtureRow[];
  bullets?: { heading: string; items: string[] }[];
  leakImages?: HazardDetailLeakImage[];
};

/** Mobile `HazardContainer` body — accordion sections on web. */
export type HazardDetailModel = {
  pageTitle: string;
  productSubType: string | null;
  storageName: string | null;
  showStorageNameHeader: boolean;
  sdsTitle: string | null;
  /** Hazard row id — catalog `files[]` key for the SDS PDF. */
  sdsFileId: string | null;
  sdsFileExt?: string;
  sdsFileName?: string;
  sections: HazardDetailSection[];
};

function parseLeakImages(
  product: Record<string, unknown>,
  path: string,
  group: HazardDetailLeakImage["group"],
): HazardDetailLeakImage[] {
  const raw = product[path] ?? product[path.charAt(0).toUpperCase() + path.slice(1)];
  if (!Array.isArray(raw)) return [];
  const out: HazardDetailLeakImage[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const id = hazardStr(r.id ?? r.Id);
    if (!id) continue;
    out.push({
      id,
      title: hazardStr(r.title ?? r.Title),
      description: hazardStr(r.description ?? r.Description),
      group,
      fileExt: hazardStr(r.fileExt ?? r.FileExt) || undefined,
    });
  }
  return out;
}

function buildLeakRecognitionSection(product: Record<string, unknown>): HazardDetailSection | null {
  const htmlBlocks: HazardDetailHtmlBlock[] = [];
  for (const block of [
    hazardHtmlBlock("Appearance", product.appearance ?? product.Appearance),
    hazardHtmlBlock("Character", product.character ?? product.Character),
    hazardHtmlBlock(
      "Leak Recognition Description",
      product.leakRecognitionDescription ?? product.LeakRecognitionDescription,
    ),
  ]) {
    if (block) htmlBlocks.push(block);
  }

  const leakImages = [
    ...parseLeakImages(product, "leakRecognitionSight", "sight"),
    ...parseLeakImages(product, "leakRecognitionSmell", "smell"),
    ...parseLeakImages(product, "leakRecognitionSound", "sound"),
  ];

  if (!htmlBlocks.length && !leakImages.length) return null;
  return {
    id: "leakRecognition",
    title: "Leak Recognition",
    htmlBlocks: htmlBlocks.length ? htmlBlocks : undefined,
    leakImages: leakImages.length ? leakImages : undefined,
  };
}

function buildProductDetailsSection(hz: Record<string, unknown>): HazardDetailSection | null {
  const product = hazardProductRow(hz);
  if (!product) return null;

  const casProps = product.cASEASProperties ?? product.CASEASProperties;
  const fields: HazardDetailFieldRow[] = [
    { label: "ERG #", value: hazardFieldDisplayValue(product.erg ?? product.ERG) },
    {
      label: "CAS Chemical Name",
      value: hazardFieldDisplayValue(product.cASChemicalName ?? product.CASChemicalName),
    },
    { label: "CAS#", value: hazardFieldDisplayValue(product.cASNumber ?? product.CASNumber) },
    {
      label: "CAS/EAS Properties",
      value: hazardFieldDisplayValue(casProps),
    },
    {
      label: "CAS# Availability",
      value: hazardFieldDisplayValue(
        product.cASNumberAvailability ??
          product.CASNumberAvailability ??
          product.casNumberAvailability,
      ),
    },
    { label: "EAS", value: hazardFieldDisplayValue(product.ehs ?? product.EHS) },
    { label: "EAS Name", value: hazardFieldDisplayValue(product.eHSName ?? product.EHSName) },
    {
      label: "Non-EAS Name",
      value: hazardFieldDisplayValue(product.nonEHSName ?? product.NonEHSName),
    },
    {
      label: "Pure/Mixture",
      value: hazardFieldDisplayValue(product.pureMixture ?? product.PureMixture),
    },
  ].filter((r) => r.value.trim());

  const mixtureRaw = product.mixtureSubform ?? product.MixtureSubform;
  const mixture: HazardDetailMixtureRow[] = [];
  if (Array.isArray(mixtureRaw)) {
    for (const row of mixtureRaw) {
      if (!row || typeof row !== "object") continue;
      const r = row as Record<string, unknown>;
      const pct = hazardStr(r.percentage ?? r.Percentage);
      mixture.push({
        productName:
          hazardStr(r.productName ?? r.ProductName) ||
          hazardStr(r.chemical ?? r.Chemical),
        percentage: pct ? `${pct}%` : "",
        casNumber: hazardStr(r.casNumber ?? r.CASNumber) || "-",
        ehs:
          r.ehs !== undefined || r.EHS !== undefined
            ? hazardStr(r.ehs ?? r.EHS) || "-"
            : "-",
      });
    }
  }

  if (!fields.length && !mixture.length) return null;
  return {
    id: "productDetails",
    title: "Product Description Details",
    fields: fields.length ? fields : undefined,
    mixture: mixture.length ? mixture : undefined,
  };
}

function buildHazardsSection(hz: Record<string, unknown>): HazardDetailSection | null {
  const product = hazardProductRow(hz);
  const htmlBlocks: HazardDetailHtmlBlock[] = [];
  const desc = product
    ? hazardStr(product.productHazardsDescription ?? product.ProductHazardsDescription)
    : "";
  const hazardDesc = hazardHtmlBlock("Hazard Description", desc);
  if (hazardDesc) htmlBlocks.push(hazardDesc);

  const physical = hazardStrList(hz.physicalHazards ?? hz.PhysicalHazards);
  const health = hazardStrList(hz.healthHazards ?? hz.HealthHazards);
  const specificRaw = hz.specificHazardsSubform ?? hz.SpecificHazardsSubform;
  const specificHtml: string[] = [];
  if (Array.isArray(specificRaw)) {
    for (const row of specificRaw) {
      if (!row || typeof row !== "object") continue;
      const html = rowHtmlFromContentField(row, "content");
      if (html.trim()) specificHtml.push(compactRichHtml(html));
    }
  }
  if (specificHtml.length) {
    htmlBlocks.push({
      heading: "Specific Hazards",
      html: specificHtml.join(
        '<hr style="margin:1rem 0;border:none;border-top:1px solid #e2e8f0"/>',
      ),
    });
  }

  const bullets: { heading: string; items: string[] }[] = [];
  if (physical.length) bullets.push({ heading: "Physical Hazards", items: physical });
  if (health.length) bullets.push({ heading: "Health Hazards", items: health });

  if (!htmlBlocks.length && !bullets.length) return null;
  return {
    id: "hazards",
    title: "Hazards",
    htmlBlocks: htmlBlocks.length ? htmlBlocks : undefined,
    bullets: bullets.length ? bullets : undefined,
  };
}

function buildStorageLocationSection(hz: Record<string, unknown>): HazardDetailSection | null {
  const name = effectiveHazardStorageName(hz);
  const lat = hazardStr(hz.storageLat ?? hz.StorageLat);
  const lng = hazardStr(hz.storageLong ?? hz.StorageLong);
  const desc = hazardStr(hz.storageDescription ?? hz.StorageDescription);
  const typeofStorage = hazardStr(
    hz.typeofStorageConditions ?? hz.TypeofStorageConditions ?? hz.typeOfStorageConditions,
  );
  const pressure = hazardStr(hz.pressure ?? hz.Pressure);
  const temperature = hazardStr(hz.temperature ?? hz.Temperature);

  const storageFields: HazardDetailFieldRow[] = [];
  if (name) storageFields.push({ label: "Location Name", value: name });
  if (lat && lng) storageFields.push({ label: "Lat / Long", value: `${lat}, ${lng}` });
  if (desc) storageFields.push({ label: "Location Description", value: desc });
  if (typeofStorage) {
    storageFields.push({ label: "Type of Storage Condition", value: typeofStorage });
  }
  if (pressure) storageFields.push({ label: "Pressure", value: pressure });
  if (temperature) storageFields.push({ label: "Temperature", value: temperature });

  const amount = hz.amountStored ?? hz.AmountStored;
  const units = hazardStr(hz.amountStoredUnits ?? hz.AmountStoredUnits);
  const amountStored =
    hazardHasStoredAmount(hz)
      ? `${hazardStr(amount)}${units ? ` ${units}` : ""}`
      : "";

  storageFields.push(
    { label: "Map Code", value: hazardStr(hz.storageMapCode ?? hz.StorageMapCode) },
    { label: "Safety Distance", value: hazardStr(hz.safetyDistance ?? hz.SafetyDistance) },
    { label: "Maximum Amount Stored", value: amountStored },
    {
      label: "Average Daily Amount",
      value: hazardStr(hz.averageDailyAmount ?? hz.AverageDailyAmount),
    },
    {
      label: "Number of Days On Site",
      value: hazardStr(
        hz.numberofDaysonSite ??
          hz.NumberofDaysonSite ??
          hz.numberOfDaysOnSite ??
          hz.NumberOfDaysOnSite,
      ),
    },
    {
      label: "Confidential",
      value: hazardStr(hz.storageConfidential ?? hz.StorageConfidential),
    },
    {
      label: "Below Reporting Thresholds",
      value: hazardStr(hz.belowReportingThresholds ?? hz.BelowReportingThresholds),
    },
    {
      label: "State Reporting",
      value: hazardStr(hz.stateReportingRequirements ?? hz.StateReportingRequirements),
    },
  );

  const tableFields = storageFields.filter((r) => r.value.trim());
  if (!tableFields.length && !hazardHasStorageLocation(hz)) return null;

  const stackedLabels = new Set([
    "Location Name",
    "Lat / Long",
    "Location Description",
    "Type of Storage Condition",
    "Pressure",
    "Temperature",
  ]);
  const stackedFields = tableFields.filter((r) => stackedLabels.has(r.label));
  const dataCardFields = tableFields.filter((r) => !stackedLabels.has(r.label));

  return {
    id: "storageLocation",
    title: "Storage Location",
    stackedFields: stackedFields.length ? stackedFields : undefined,
    fields: dataCardFields.length ? dataCardFields : undefined,
  };
}

function buildGeneralResponseSection(hz: Record<string, unknown>): HazardDetailSection | null {
  const product = hazardProductRow(hz);
  if (!product) return null;
  const html = hazardStr(product.generalResponse ?? product.GeneralResponse);
  if (!html.trim()) return null;
  return {
    id: "generalResponse",
    title: "General Response",
    html: html.includes("<")
      ? compactRichHtml(html)
      : `<p style="margin:0;line-height:1.5">${esc(html)}</p>`,
  };
}

export function buildAssetHazardDetailModel(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
  activeAssetId: string | null | undefined,
  hazardId: string | null | undefined,
): HazardDetailModel | null {
  const id = hazardId?.trim() ?? "";
  if (!id) return null;
  let asset = findActiveAssetRow(payload, ctx, activeAssetId);
  let hz = asset ? findHazardRowInAsset(asset, id) : null;
  if (!hz) {
    const resolvedAssetId = findAssetIdByHazardId(payload, ctx, id);
    if (resolvedAssetId) {
      asset = findActiveAssetRow(payload, ctx, resolvedAssetId);
      hz = asset ? findHazardRowInAsset(asset, id) : null;
    }
  }
  if (!asset || !hz) return null;

  const product = hazardProductRow(hz);
  const productSubType = product
    ? hazardStr(product.productSubType ?? product.ProductSubType) || null
    : null;
  const storageName = effectiveHazardStorageName(hz) || null;
  const pageTitle = productSubType || storageName || hazardLabel(hz);
  const sdsTitle = hazardStr(hz.sDSTitle ?? hz.SDSTitle) || null;
  const hazardFileId = hazardIdFromRow(hz);
  let sdsFileExt = docFileExt(hz);
  if (sdsTitle && hazardFileId && ctx.catalogOperatorRow) {
    const catalogEntry = findCatalogFileEntry(ctx.catalogOperatorRow, hazardFileId, sdsFileExt);
    if (catalogEntry?.ext) {
      sdsFileExt = catalogEntry.ext;
    }
  }

  const sections: HazardDetailSection[] = [];
  if (product) {
    const leak = buildLeakRecognitionSection(product);
    if (leak) sections.push(leak);
  }
  const productDetails = buildProductDetailsSection(hz);
  if (productDetails) sections.push(productDetails);
  const hazards = buildHazardsSection(hz);
  if (hazards) sections.push(hazards);
  const storage = buildStorageLocationSection(hz);
  if (storage) sections.push(storage);
  const general = buildGeneralResponseSection(hz);
  if (general) sections.push(general);

  return {
    pageTitle,
    productSubType,
    storageName,
    showStorageNameHeader: isFacilityAsset(asset),
    sdsTitle,
    sdsFileId: sdsTitle && hazardFileId ? hazardFileId : null,
    sdsFileExt: sdsFileExt ?? undefined,
    sdsFileName: sdsTitle && hazardFileId ? docPayloadFileName(hz, sdsTitle) : undefined,
    sections,
  };
}

export function hazardDetailTitleFromRow(hz: Record<string, unknown>): string {
  const product = hazardProductRow(hz);
  const sub = product
    ? hazardStr(product.productSubType ?? product.ProductSubType)
    : "";
  if (sub) return sub;
  const storage = effectiveHazardStorageName(hz);
  if (storage) return storage;
  return hazardLabel(hz);
}
